// useToken.js
import { useEffect, useRef, useState } from "react";
import { getAuth, onIdTokenChanged, signOut } from "firebase/auth";
import { initializeApp, getApps, getApp } from "firebase/app";
import firebaseConfig from "@serie3/mymediaplanner/Config/Firebase/firebase";

// --- Initialize Firebase once (singleton) ---
const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
const auth = getAuth(app);

export default function useToken() {
  const safeParse = (str, fallback) => {
    try {
      return str ? JSON.parse(str) : fallback;
    } catch {
      return fallback;
    }
  };

  const [token, setToken] = useState(() => safeParse(localStorage.getItem("mmp_token"), null));
  const [roles, setRoles] = useState(() => safeParse(localStorage.getItem("roles"), []));
  const isLoggingOutRef = useRef(false);
  const refreshTimeoutRef = useRef(null);

  const dispatchTokenEvent = (userToken, localId) => {
    window.dispatchEvent(
      new CustomEvent("tokenAvailableMediaplanner", {
        detail: {
          token: userToken,
          localId,
          sourceURL: window.location.href,
          loginTime: Date.now(),
        },
      })
    );
  };

  const applyLoggedOutState = () => {
    window.dispatchEvent(new CustomEvent("logoutMediaplanner"));
    localStorage.removeItem("mmp_token");
    localStorage.removeItem("mmp_localId");
    localStorage.removeItem("mmp_loginTime");
    localStorage.removeItem("roles");
    setToken(null);
    setRoles([]);
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
  };

  const logout = async (redirect = true) => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;
    try {
      await signOut(auth); // ensure Firebase clears its persisted refresh token
    } catch (e) {
      console.warn("signOut failed:", e);
    } finally {
      applyLoggedOutState();
      if (redirect && window.location.pathname !== "/") {
        window.location.assign("/");
      }
      // allow subsequent logouts after the auth listener settles
      setTimeout(() => {
        isLoggingOutRef.current = false;
      }, 0);
    }
  };

  const saveToken = (userToken, localId, newRoles = [], isRefresh = true) => {
    if (!userToken || !localId) return logout(true);

    localStorage.setItem("mmp_token", userToken);
    localStorage.setItem("mmp_localId", localId);
    localStorage.setItem("roles", JSON.stringify(newRoles));
    localStorage.setItem("mmp_loginTime", String(Date.now()));

    setToken(userToken);
    setRoles(newRoles);
    dispatchTokenEvent(userToken, localId);

    // Redirect only on *initial* login (pass isRefresh=false from your login flow)
    if (!isRefresh && window.location.pathname !== "/") {
      window.location.assign("/");
    }
  };

  useEffect(() => {
    const scheduleRefresh = async (user) => {
      
      if (!user) return;
      try {
        const { expirationTime } = await user.getIdTokenResult();
        const expMs = new Date(expirationTime).getTime();
        // refresh 2 minutes before expiry (at least 30s from now)
        const delay = Math.max(expMs - Date.now() - 2 * 60 * 1000, 30 * 1000);
        if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = setTimeout(async () => {
          try {
            await user.getIdToken(true); // force refresh; onIdTokenChanged will persist it
          } catch (e) {
            console.error("Forced token refresh failed:", e);
            logout(true);
          }
        }, delay);
      } catch (e) {
        console.error("Failed to schedule refresh:", e);
      }
    };

    const unsub = onIdTokenChanged(auth, async (user) => {
      if (!user) {
        // Signed out — clear state but don't redirect here (avoid loops)
        if (!isLoggingOutRef.current) applyLoggedOutState();
        return;
      }

      try {
        const idToken = await user.getIdToken(); // current token (SDK may have just refreshed)
        const storedRoles = safeParse(localStorage.getItem("roles"), []);
        saveToken(idToken, user.uid, storedRoles, /* isRefresh */ true);
        await scheduleRefresh(user);
      } catch (e) {
        console.error("onIdTokenChanged error:", e);
        if (!isLoggingOutRef.current) applyLoggedOutState();
      }
    });

    // Allow the extension to request the current token at any time
    const onTokenRequest = () => {
      const storedToken = localStorage.getItem("mmp_token");
      const storedLocalId = localStorage.getItem("mmp_localId");
      if (storedToken && storedLocalId) {
        window.dispatchEvent(
          new CustomEvent("tokenAvailableMediaplanner", {
            detail: { token: storedToken, localId: storedLocalId },
          })
        );
      }
    };
    window.addEventListener("requestTokenMediaplanner", onTokenRequest);

    // If tab becomes visible again (timers can pause), refresh proactively
    const onVis = async () => {
      if (document.visibilityState === "visible" && auth.currentUser) {
        try {
          await auth.currentUser.getIdToken(true);
        } catch (e) {
          console.warn("Visibility refresh failed:", e);
          logout(true);
        }
      }
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      unsub();
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("requestTokenMediaplanner", onTokenRequest);
      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    token,
    roles,
    setToken: saveToken, // call with (idToken, uid, rolesArray, /*isRefresh=*/false) on initial login
    logout,
  };
}
